import {
	Blockquote,
	Box,
	Code,
	Highlight,
	List,
	Table,
	Text,
	ThemeIcon,
	Title,
} from '@mantine/core';
import { IconCircleCheck } from '@tabler/icons-react';
import React from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styles from './styles.module.css';

/**
 * MarkdownComponent renders GitHub Flavored Markdown content
 *
 * This component uses react-markdown with the remark-gfm plugin to parse and
 * render markdown content with GitHub Flavored Markdown support, including:
 * - Tables
 * - Strikethrough
 * - Task lists
 * - URLs are automatically linked
 *
 * The component can be customized by providing custom renderers for specific HTML elements
 * through the optional `customComponents` prop.
 *
 * @param {Object} props - Component props
 * @param {string} props.content - Markdown string to be rendered
 * @param {Object} [props.customComponents] - Optional custom components for rendering specific elements
 * @returns {JSX.Element} Rendered markdown content
 */
export const MarkdownComponent = ({
	content,
	customComponents,
}: {
	content: string;
	customComponents?: React.ComponentProps<typeof Markdown>['components'];
}): JSX.Element => {
	// Default components using Mantine UI
	const defaultComponents: React.ComponentProps<typeof Markdown>['components'] = {
		// Text elements
		p: ({ children }) => (
			<Text size="sm" my="sm">
				{children}
			</Text>
		),

		// Headings with Mantine Title
		h1: ({ children }) => (
			<Title order={1} mt="xl" mb="md">
				{children}
			</Title>
		),
		h2: ({ children }) => (
			<Title order={2} mt="lg" mb="sm">
				{children}
			</Title>
		),
		h3: ({ children }) => (
			<Title order={3} mt="md" mb="xs">
				{children}
			</Title>
		),
		h4: ({ children }) => (
			<Title order={4} mt="md" mb="xs">
				{children}
			</Title>
		),
		h5: ({ children }) => (
			<Title order={5} mt="md" mb="xs">
				{children}
			</Title>
		),
		h6: ({ children }) => (
			<Title order={6} mt="md" mb="xs">
				{children}
			</Title>
		),

		// Blockquote with Mantine Blockquote
		blockquote: ({ children }) => <Blockquote my="md">{children}</Blockquote>,

		// Code blocks with Mantine Code
		code: ({ children, ...props }) => {
			// Type assertion for the inline property
			const { inline } = props as { inline?: boolean };

			return !inline ? (
				<Code block my="md">
					{children}
				</Code>
			) : (
				<Code>{children}</Code>
			);
		},

		// Highlighted text with Mantine Highlight
		em: ({ children }) => {
			const content = children ? String(children) : '';
			return <Highlight highlight={content}>{content}</Highlight>;
		},

		// Lists with Mantine List
		ul: ({ children }) => (
			<List size="sm" my="md">
				{children}
			</List>
		),
		ol: ({ children }) => (
			<List size="sm" my="md" type="ordered" className={styles.md_ol}>
				{children}
			</List>
		),
		li: ({ children, ...props }) => {
			// Type assertion for the checked property
			const { checked } = props as { checked?: boolean };

			if (checked === undefined) {
				return <List.Item className={styles.md_li}>{children}</List.Item>;
			}

			// For task lists (checkboxes)
			return (
				<List.Item
					className={styles.md_li}
					icon={
						checked ? (
							<ThemeIcon color="green" size={20} radius="xl">
								<IconCircleCheck size={14} />
							</ThemeIcon>
						) : (
							<ThemeIcon color="gray" size={20} radius="xl">
								<Box w={14} h={14} />
							</ThemeIcon>
						)
					}
				>
					{children}
				</List.Item>
			);
		},

		// Tables with Mantine Table
		table: ({ children }) => (
			<div className={styles.md_table_wrapper}>
				<Table
					my="lg"
					horizontalSpacing="xs"
					verticalSpacing="xs"
					withTableBorder
					className={styles.md_table}
				>
					{children}
				</Table>
			</div>
		),
		tbody: ({ children }) => <Table.Tbody>{children}</Table.Tbody>,
		thead: ({ children }) => <Table.Thead className={styles.md_thead}>{children}</Table.Thead>,
		tr: ({ children }) => <Table.Tr className={styles.md_tr}>{children}</Table.Tr>,
		td: ({ children }) => <Table.Td>{children}</Table.Td>,
		th: ({ children }) => <Table.Th>{children}</Table.Th>,

		// Links
		a: ({ children, href }) => (
			<Text
				component="a"
				href={href}
				variant="link"
				target="_blank"
				rel="noopener noreferrer"
				className={styles.md_link}
			>
				{children}
			</Text>
		),
	};

	// Merge default components with any custom components provided
	const components = { ...defaultComponents, ...customComponents };

	return (
		<Markdown remarkPlugins={[remarkGfm]} components={components}>
			{content}
		</Markdown>
	);
};
